import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import API_BASE_URL from "../config";

/**
 * Modal reutilizable para asignar operador(es) "de apoyo" a un vuelo.
 * Reemplaza la mala práctica de registrar un servicio y luego marcarlo NOSHOW.
 * Se usa en las páginas de aerolíneas NO-AM (AA/UA/DL/Integrado).
 *
 * Props:
 *  - vuelo: objeto del vuelo seleccionado (o null para cerrado)
 *  - aerolineaFallback: código de aerolínea si el vuelo no lo trae
 *  - onClose: () => void
 */
const ModalApoyoVuelo = ({ vuelo, aerolineaFallback = null, onClose }) => {
  const token = useSelector((state) => state.auth.token);
  const decoded = token ? jwtDecode(token) : null;
  const estacion = decoded?.estacion?.trim().toUpperCase();

  const [colaboradores, setColaboradores] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);
  const [observaciones, setObservaciones] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [cargando, setCargando] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!vuelo) return;
    setSeleccionados([]);
    setObservaciones("");
    setBusqueda("");
    const fetchOperadores = async () => {
      try {
        setCargando(true);
        const res = await axios.get(`${API_BASE_URL}api/bitacora/en-turno-detalles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const disponibles = (res.data || []).filter(
          (c) => c.estacion?.trim().toUpperCase() === estacion
        );
        setColaboradores(disponibles);
      } catch (error) {
        toast.error("Error al obtener operadores");
      } finally {
        setCargando(false);
      }
    };
    fetchOperadores();
  }, [vuelo, token, estacion]);

  if (!vuelo) return null;

  const toggle = (c) => {
    setSeleccionados((prev) =>
      prev.some((op) => op.noColaborador === c.noColaborador)
        ? prev.filter((op) => op.noColaborador !== c.noColaborador)
        : [...prev, c]
    );
  };

  const asignarApoyo = async () => {
    if (isSubmitting) return;
    if (seleccionados.length === 0) {
      toast.warning("⚠️ Selecciona al menos un operador de apoyo");
      return;
    }
    setIsSubmitting(true);
    try {
      const horaVuelo = moment(
        vuelo.tipoServicio === "LLEGADAS"
          ? vuelo.published_arrival_time
          : vuelo.published_departure_time
      ).format("YYYY-MM-DD HH:mm:ss");

      await axios.post(
        `${API_BASE_URL}api/servicios/apoyo-vuelo`,
        {
          operadores: seleccionados.map((c) => ({
            noColaborador: c.noColaborador,
            siglas: c.siglas,
          })),
          observaciones,
          aerolinea: vuelo.aerolinea || aerolineaFallback,
          noVuelo: vuelo.flight_number,
          origenVuelo: vuelo.latest_departure_airport,
          destinoVuelo: vuelo.latest_arrival_airport,
          tipoVuelo: vuelo.tipoServicio,
          int_nac: vuelo.tipoVuelo,
          estacion,
          horaVuelo,
          sala: vuelo.sala || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("✅ Apoyo(s) asignado(s) correctamente");
      onClose();
    } catch (error) {
      console.error("❌ Error al asignar apoyo:", error);
      toast.error(error.response?.data?.error || "Error al asignar apoyo");
    } finally {
      setIsSubmitting(false);
    }
  };

  const visibles = colaboradores.filter((c) =>
    (c.siglas || "").toUpperCase().includes(busqueda)
  );

  return (
    <div className="modal-overlay-pasajeros" onClick={onClose}>
      <div className="modal-pasajeros" onClick={(e) => e.stopPropagation()}>
        <h3>Asignar apoyo al vuelo {vuelo.flight_number}</h3>
        <p style={{ margin: "4px 0 12px", color: "#64748b", fontSize: "0.9rem" }}>
          Los apoyos NO generan noshow. El operador recibe la asignación en su handheld.
        </p>

        <input
          type="text"
          placeholder="Buscar por siglas..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value.toUpperCase())}
          className="input-busqueda"
          style={{ marginBottom: "10px", width: "100%", padding: "6px" }}
        />

        <div className="tabla-scroll" style={{ maxHeight: "320px", overflowY: "auto" }}>
          {cargando ? (
            <p style={{ padding: 12 }}>Cargando operadores...</p>
          ) : (
            <table className="tabla-pasajeros">
              <thead>
                <tr>
                  <th></th>
                  <th>#</th>
                  <th>Nombre</th>
                  <th>Siglas</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((c, idx) => (
                  <tr key={c.noColaborador}>
                    <td>
                      <input
                        type="checkbox"
                        checked={seleccionados.some((op) => op.noColaborador === c.noColaborador)}
                        onChange={() => toggle(c)}
                      />
                    </td>
                    <td>{idx + 1}</td>
                    <td>{c.nombreCompleto}</td>
                    <td>{c.siglas}</td>
                  </tr>
                ))}
                {visibles.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", padding: 12 }}>
                      Sin operadores en turno.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ margin: "12px 0" }}>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            Observaciones
          </label>
          <textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder="Ej. apoyo por alto volumen de sillas"
            rows={2}
            style={{ width: "100%", padding: 8, resize: "vertical" }}
          />
        </div>

        <div className="text-end">
          <button className="btn-registrar" onClick={asignarApoyo} disabled={isSubmitting}>
            {isSubmitting ? "Asignando..." : "Asignar apoyo"}
          </button>
          <button className="btn-cerrar" onClick={onClose} disabled={isSubmitting}>
            ✖ Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalApoyoVuelo;
